import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Clock, AlertCircle, DollarSign, FileText, Building2, Users, CreditCard, ClipboardCheck, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { useNotifications } from '../../context/NotificationContext';
import { cn } from '../../lib/utils';

const getNotificationIcon = (type, refType) => {
  // Use reference_type for more accurate icon
  if (refType === 'booking') return <ClipboardCheck className="h-4 w-4 text-indigo-600" />;
  if (refType === 'payment') return <CreditCard className="h-4 w-4 text-emerald-600" />;
  if (refType === 'kyc') return <Users className="h-4 w-4 text-purple-600" />;
  if (refType === 'appointment') return <MapPin className="h-4 w-4 text-orange-600" />;
  if (refType === 'property') return <Building2 className="h-4 w-4 text-blue-600" />;
  if (refType === 'commission_claim') return <DollarSign className="h-4 w-4 text-green-600" />;
  
  switch (type) {
    case 'commission_update':
      return <DollarSign className="h-4 w-4 text-green-600" />;
    case 'claim_status':
      return <FileText className="h-4 w-4 text-blue-600" />;
    case 'appointment_status':
      return <Clock className="h-4 w-4 text-orange-600" />;
    case 'system':
      return <AlertCircle className="h-4 w-4 text-gray-600" />;
    default:
      return <Bell className="h-4 w-4 text-gray-600" />;
  }
};

// Resolve notification click to a route
const getNotificationRoute = (notification) => {
  const { reference_type, reference_id } = notification;
  switch (reference_type) {
    case 'booking':
      // Navigate to admin bookings list (no detail page exists)
      return '/admin/bookings';
    case 'payment':
      return '/admin/bookings';
    case 'commission_claim':
      // Navigate to commission claims page
      return '/admin/commission-claims';
    case 'appointment':
      return '/my-appointments';
    case 'kyc':
      return '/admin/kyc';
    case 'property':
      return reference_id ? `/properties/${reference_id}` : '/admin/properties';
    case 'agent':
      return reference_id ? `/admin/agents/${reference_id}` : '/admin/agents';
    default:
      return null;
  }
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, isConnected, markAsRead, markAllAsRead } = useNotifications();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              variant="destructive"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          {/* Connection indicator */}
          <span 
            className={cn(
              "absolute bottom-0 right-0 h-2 w-2 rounded-full",
              isConnected ? "bg-green-500" : "bg-gray-400"
            )}
            title={isConnected ? "Real-time connected" : "Offline"}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-xs"
              onClick={markAllAsRead}
              data-testid="mark-all-read"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "flex gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors",
                    !notification.is_read && "bg-blue-50/50"
                  )}
                  onClick={() => {
                    if (!notification.is_read) {
                      markAsRead(notification.id);
                    }
                    // Navigate to the relevant page based on notification type
                    const route = getNotificationRoute(notification);
                    if (route) {
                      navigate(route);
                      setIsOpen(false);
                    }
                  }}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.notification_type, notification.reference_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn(
                        "text-sm",
                        !notification.is_read && "font-semibold"
                      )}>
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <Button 
                variant="ghost" 
                className="w-full text-sm"
                onClick={() => {
                  navigate('/notifications');
                  setIsOpen(false);
                }}
              >
                View all notifications
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
